import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { AlertCircle, TrendingUp, FileText, AlertTriangle, Info, Sparkles, Loader2 } from "lucide-react";

interface StructuredInsight {
  type?: string;
  title?: string;
  message?: string;
  clinical_relevance?: string;
}

interface AnalysisCardProps {
  summary?: string | null;
  insights?: StructuredInsight[] | string[] | null;
  riskLevel?: string | null;
  labResults?: string | null;
}

interface AnalysisCardExtraProps {
  onRegenerate?: () => void;
  isRegenerating?: boolean;
}

export function AnalysisCard({
  summary,
  insights,
  riskLevel,
  labResults,
  onRegenerate,
  isRegenerating
}: AnalysisCardProps & AnalysisCardExtraProps) {
  const getRiskBadge = (level?: string | null) => {
    if (!level) return null;

    const configs = {
      alto: { variant: "destructive" as const, label: "Alto Risco", icon: AlertCircle },
      moderado: { variant: "default" as const, label: "Risco Moderado", icon: TrendingUp },
      baixo: { variant: "secondary" as const, label: "Baixo Risco", icon: TrendingUp }
    };

    const config = configs[level as keyof typeof configs] || configs.baixo;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const insightsList: (StructuredInsight | string)[] = Array.isArray(insights) ? insights : [];
  const isStructured = insightsList.length > 0 && typeof insightsList[0] === 'object' && (insightsList[0] as StructuredInsight).title;

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'alert': return AlertCircle;
      case 'warning': return AlertTriangle;
      case 'info': return Info;
      default: return Info;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'alert': return 'text-destructive';
      case 'warning': return 'text-yellow-600';
      case 'info': return 'text-blue-600';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Análise da IA</CardTitle>
          <div className="flex items-center gap-2">
            {getRiskBadge(riskLevel)}
            {onRegenerate && (
              <Button
                variant="outline"
                size="sm"
                onClick={onRegenerate}
                disabled={isRegenerating}
              >
                {isRegenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Gerando...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Gerar Nova Análise
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {summary && (
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Resumo
            </h4>
            <p className="text-sm text-muted-foreground">{summary}</p>
          </div>
        )}

        {insightsList.length > 0 && (
          <>
            <Separator />
            <div>
              <h4 className="font-semibold mb-3">Insights Clínicos</h4>
              {isStructured ? (
                <div className="space-y-3">
                  {insightsList.map((insight, idx: number) => {
                    const structuredInsight = insight as StructuredInsight;
                    const Icon = getInsightIcon(structuredInsight.type || '');
                    const colorClass = getInsightColor(structuredInsight.type || '');

                    return (
                      <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border">
                        <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${colorClass}`} />
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center justify-between gap-2">
                            <h5 className="font-medium text-sm">{structuredInsight.title}</h5>
                            {structuredInsight.clinical_relevance && (
                              <Badge variant="outline" className="text-xs">
                                {structuredInsight.clinical_relevance}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{structuredInsight.message}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <ul className="space-y-2">
                  {insightsList.map((insight: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className="text-secondary mt-1">•</span>
                      <span>{insight}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        {labResults && (
          <>
            <Separator />
            <div>
              <h4 className="font-semibold mb-2">Dados Consolidados</h4>
              <div className="border rounded-lg p-3 bg-muted/50">
                <pre className="whitespace-pre-wrap text-sm">{labResults}</pre>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
